import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, tap } from 'rxjs';
import { UserModel } from '../models/user.model';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { UserCredentials, UserRegistration } from 'src/app/core/interfaces/user-credentials.interface';
import { AuthSyncService } from 'src/app/core/services/async-auth.service';
import { JwtService } from 'src/app/core/services/jwt.service';
import { DropdownInputsSignalService } from 'src/app/core/services/dropdown-inputs-signal.service';

export type UserType = UserModel | undefined;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private API_USERS_URL = `${environment.apiBaseUrl}/Authentication`;

  currentUser$: Observable<UserType>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;
  user: UserModel;

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private router: Router,
    private http: HttpClient,
    private authAsyncService: AuthSyncService,
    private jwtService: JwtService,
    private signalService: DropdownInputsSignalService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isLoading$ = this.isLoadingSubject.asObservable();
  }

  login(data: UserCredentials) {
    return this.http.post(
      `${environment.apiBaseUrl}/Authentication/Admin/SignIn`,
      data
    );
  }

  refreshToken(): Observable<any> {
    return this.http.post(
      `${environment.apiBaseUrl}/Authentication/Refresh-Token`,
      {
        accessToken: this.getAccessToken(),
        refreshToken: this.getRefreshToken(),
      }
    );
  }

  logout() {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('expireAt');
    sessionStorage.removeItem('requiresTwoFactor');
    sessionStorage.removeItem('twoFactorToken');
    this.authAsyncService.logoutAllTabs();
    this.router.navigate(['/auth/login']);
  }

  signOut(): Observable<any> {
    // استدعاء Sign-Out endpoint: /api/v1/Authentication/Sign-Out
    return this.http.post(`${environment.apiBaseUrl}/Authentication/Sign-Out`, {});
  }

  getUserProfile() {
    return this.http.get(`${environment.apiBaseUrl}/User/Profile`);
  }

  forgotPassword(userIdentifier: string) {
    return this.http.post(`${this.API_USERS_URL}/ForgetPassword`, {
      userIdentifier: userIdentifier,
    });
  }

  changePassword(input: {
    userIdentifier: string,
    CurrentPassword: string,
    ConfirmNewPassword: string,
    NewPassword: string
  }): Observable<any> {
    return this.http.post(`${this.API_USERS_URL + '/ChangePassword'}`, input);
  }

  optVerify(userIdentifier: string, code: string) {
    // Uses /api/v1/Authentication/confirm-code for OTP verification during forgot password flow
    return this.http.post(
      `${this.API_USERS_URL}/confirm-code?userIdentifier=${userIdentifier}&code=${code}`,
      {}
    );
  }

  codeVerify(userIdentifier: string, code: number) {
    return this.http.post(`${this.API_USERS_URL}/ConfirmEmail`, {
      userIdentifier: userIdentifier,
      code: code,
    });
  }

  resetPassword(
    userIdentifier: string,
    password: string,
    confirmPassword: string
  ) {
    return this.http.post(`${this.API_USERS_URL}/ResetPassword`, {
      userIdentifier: userIdentifier,
      password: password,
      confirmPassword: confirmPassword,
    });
  }

  setToken(token: string) {
    sessionStorage.setItem('accessToken', token);
  }

  setRefreshToken(token: string) {
    sessionStorage.setItem('refreshToken', token);
  }

  setTokenExpiration(expireAt: string) {
    sessionStorage.setItem('expireAt', expireAt);
  }

  setRequiresTwoFactor(value: boolean): void {
    sessionStorage.setItem('requiresTwoFactor', value ? 'true' : 'false');
  }

  getRequiresTwoFactor(): boolean {
    return sessionStorage.getItem('requiresTwoFactor') === 'true';
  }

  setTwoFactorToken(token: string): void {
    sessionStorage.setItem('twoFactorToken', token);
  }

  getTwoFactorToken(): string | null {
    return sessionStorage.getItem('twoFactorToken');
  }

  clearTwoFactorToken(): void {
    sessionStorage.removeItem('twoFactorToken');
  }

  isTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return true;
    }

    const expireAt = sessionStorage.getItem('expireAt');
    if (!expireAt) {
      return true;
    }

    const expireTime = new Date(expireAt).getTime();
    const now = Date.now();

    return now >= expireTime;
  }


  isLoggedIn(): boolean {
    const token = sessionStorage.getItem('accessToken');
    return !!token;
  }

  getAccessToken() {
    return sessionStorage.getItem('accessToken');
  }

  getRefreshToken() {
    return sessionStorage.getItem('refreshToken');
  }

  invalidateUserPremissions = (flag = false) => {
    let userId: string | null = null;
    const token = this.getAccessToken();
    if (token) {
      userId = this.jwtService.getUserId(token);
    }

    if(flag) return userId;

    // Emit an event that PermissionService can listen to
    if (!!userId) {
      this.signalService.triggerReload('invalidatePermissions');
    }
  }

}
